/**
 * Sorting for the client list columns (Client Name, Client Number, Last Updated) — KOT-128.
 *
 * The MUI DataGrid default (`gridStringOrNumberComparator`) broke on this data in two ways:
 *
 * 1. It compares raw cell values, so a leading space or a stray non-visible character sorts
 *    a client ahead of every real value. The grid derives descending as `-1 * ascending`,
 *    so those rows pile up at the *top* in ascending and at the *bottom* in descending —
 *    which is why only ascending ever looked broken.
 * 2. Client Number is a STRING column, so it sorted as text: 10, 100, 25, 3, 9.
 *
 * Normalising the value and comparing with a numeric-aware collator fixes both, and the
 * numeric collator handles a non-numeric client number (e.g. "ABC-123") without the NaN
 * that Number() coercion would produce.
 */

// numeric: digit runs compare by value, so 9 < 10 < 100 rather than "10" < "100" < "9".
// sensitivity "base": case and accents do not affect order. Same options as the correspondent
// table's collator in ClientInfo/ClientDetails.js.
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/**
 * Strips what should never influence order: zero-width characters, and leading, trailing or
 * repeated whitespace (\s already covers NBSP and the other Unicode spaces; the zero-width
 * ones are outside it and need removing explicitly).
 *
 * Null and undefined normalise to "", which the collator puts ahead of every real value in
 * ascending — the same placement the grid's own null handling gives them, so blank client
 * numbers keep the behaviour they have today.
 */
export const normalizeSortText = (value) =>
  (value ?? "")
    .toString()
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/** DataGrid `sortComparator` for Client Name and Client Number. */
export const clientSortComparator = (a, b) => collator.compare(normalizeSortText(a), normalizeSortText(b));

/**
 * Turns the API's `lastUpdatedAt` timestamp into the `Date` that the grid's date columns
 * compare (`gridDateComparator`). Anything missing or unparseable becomes `null`, which the
 * grid's own nullish handling places consistently rather than throwing an Invalid Date into
 * the comparison.
 *
 * The Last Updated column needs this because it declares `field: "lastUpdated"` while the
 * payload carries `lastUpdatedAt` — so without a valueGetter every cell value is `undefined`,
 * every comparison ties, and clicking the header does nothing in either direction.
 */
export const toSortableDate = (value) => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
